import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getDispatcherStats,
  getTenantHealth,
  listTenants,
  type DispatcherStats,
  type TenantHealthResponse,
  type TenantSummary,
} from "@/lib/api";
import { Card, ErrorBanner, PageContainer, toErrorMessage } from "@/components/Forms";
import { ClockIcon, LeadsIcon, PersonIcon } from "@/components/Icons";

/**
 * Platform admin landing — three at-a-glance KPIs (tenant count, total
 * due-leads across all tenants, count of tenants with stuck claims) plus
 * quick links into the major surfaces.
 */
export default function DashboardPage() {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [dispatcher, setDispatcher] = useState<DispatcherStats | null>(null);
  const [health, setHealth] = useState<TenantHealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listTenants().catch(() => [] as TenantSummary[]),
      getDispatcherStats().catch(() => ({ workerId: null, tenants: [] }) as DispatcherStats),
      getTenantHealth().catch(() => ({ workerId: null, tenants: [] }) as TenantHealthResponse),
    ])
      .then(([ts, d, h]) => {
        if (cancelled) return;
        setTenants(ts);
        setDispatcher(d);
        setHealth(h);
      })
      .catch((e) => !cancelled && setError(toErrorMessage(e, t("dashboard.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [t]);

  const totalDue = dispatcher?.tenants.reduce((acc, tt) => acc + (tt.dueCount ?? 0), 0) ?? 0;
  const stuckCount = dispatcher?.tenants.reduce((acc, tt) => acc + (tt.stuckClaim ?? 0), 0) ?? 0;
  const tenantCount = tenants?.length ?? 0;

  return (
    <PageContainer title={t("dashboard.title")} subtitle={t("dashboard.subtitle")}>
      <ErrorBanner message={error} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Kpi label={t("dashboard.kpiTenants")} value={tenantCount} Icon={PersonIcon} to="/tenants" />
        <Kpi label={t("dashboard.kpiQueueDepth")} value={totalDue} Icon={LeadsIcon} to="/dispatcher" />
        <Kpi
          label={t("dashboard.kpiStuckClaims")}
          value={stuckCount}
          tone={stuckCount > 0 ? "warn" : "neutral"}
          Icon={ClockIcon}
          to="/dispatcher"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <Card
          title={t("dashboard.tenantsCardTitle")}
          description={t("dashboard.tenantsCardDescription")}
          actions={<Link className="text-xs text-accent hover:underline" to="/tenants">→</Link>}
        >
          {tenants === null ? (
            <Skel />
          ) : tenants.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("dashboard.tenantsEmpty")}</p>
          ) : (
            <ul className="divide-y divide-border">
              {tenants.slice(0, 6).map((tt) => (
                <li key={tt.slug} className="py-2.5 text-sm">
                  <Link to={`/tenants/${tt.slug}`} className="flex items-center justify-between hover:underline">
                    <span>
                      <span className="font-mono">{tt.slug}</span>
                      <span className="text-muted-foreground"> · {tt.name}</span>
                    </span>
                    <span className="text-xs text-muted-foreground">{tt.vertical}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={t("nav.health")} actions={<Link className="text-xs text-accent hover:underline" to="/health">→</Link>}>
          {health === null ? (
            <Skel />
          ) : health.tenants.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("health.empty")}</p>
          ) : (
            <ul className="divide-y divide-border">
              {health.tenants.slice(0, 6).map((tt) => (
                <li key={tt.slug} className="py-2.5 flex items-center justify-between text-sm">
                  <span className="font-mono truncate">{tt.slug}</span>
                  <span className="flex items-center gap-2 text-xs">
                    <span className={`inline-block h-2 w-2 rounded-full ${tt.status === "UP" ? "bg-emerald-400" : "bg-destructive"}`} />
                    {tt.status}
                    <span className="text-muted-foreground">· {tt.dueCount ?? 0}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </PageContainer>
  );
}

function Kpi({
  label,
  value,
  Icon,
  to,
  tone = "neutral",
}: {
  label: string;
  value: number;
  Icon: (p: { size?: number | string }) => JSX.Element;
  to?: string;
  tone?: "neutral" | "warn";
}) {
  const valueTone = tone === "warn" && value > 0 ? "text-amber-400" : "text-foreground";
  const card = (
    <article className="rounded-xl border border-border bg-card p-4 transition hover:border-foreground/20">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-accent/15 text-accent">
          <Icon size={16} />
        </span>
      </div>
      <p className={`mt-3 text-3xl font-bold tracking-tight ${valueTone}`}>{value}</p>
    </article>
  );
  return to ? <Link to={to}>{card}</Link> : card;
}

function Skel() {
  return (
    <div className="space-y-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-10 rounded-md bg-muted/40 animate-pulse" />
      ))}
    </div>
  );
}
